import { cn } from "cn";
import {
  BwCardAlipay,
  BwCardAmex,
  BwCardDiners,
  BwCardDiscover,
  BwCardElo,
  BwCardHiper,
  BwCardHipercard,
  BwCardJcb,
  BwCardMaestro,
  BwCardMir,
  BwCardUnionpay,
  BwPaymentMethodApplePayDark,
  BwPaymentMethodApplePayLight,
  BwPaymentMethodBankDark,
  BwPaymentMethodBankLight,
  BwPaymentMethodCashDark,
  BwPaymentMethodCashLight,
  BwPaymentMethodCcGenericDark,
  BwPaymentMethodCcGenericLight,
  BwPaymentMethodCreditsDark,
  BwPaymentMethodCreditsLight,
  BwPaymentMethodDepositDark,
  BwPaymentMethodDepositLight,
  BwPaymentMethodGooglePayDark,
  BwPaymentMethodGooglePayLight,
  BwPaymentMethodMasterCardDark,
  BwPaymentMethodMasterCardLight,
  BwPaymentMethodPaypalDark,
  BwPaymentMethodPaypalLight,
  BwPaymentMethodVisaDark,
  BwPaymentMethodVisaLight,
} from "@/components/ui/icons";
import ThemeIcon from "@/components/ui/icons/theme-icon";
import { PaymentMethodType } from "../_types/payment-method-type";

type PaymentMethodIconProps = {
  /** The card brand name or bank name. */
  brand: string;

  /** The payment method type: "card" or "us_bank_account". */
  type: PaymentMethodType | null;

  /** Additional CSS classes for the icon container. */
  className?: string;
};

/**
 * Renders the appropriate icon for a payment method: a card brand icon for cards
 * or a bank icon for US bank accounts.
 */

export const PaymentMethodIcon = ({
  brand,
  type,
  className,
}: PaymentMethodIconProps) => {
  switch (type) {
    case PaymentMethodType.UsBankAccount:
      return (
        <ThemeIcon
          lightIcon={BwPaymentMethodBankLight}
          darkIcon={BwPaymentMethodBankDark}
          className={cn("h-10 w-15", className)}
        />
      );
    case PaymentMethodType.Cash:
      return (
        <ThemeIcon
          lightIcon={BwPaymentMethodCashLight}
          darkIcon={BwPaymentMethodCashDark}
          className={cn("h-10 w-15", className)}
        />
      );
    case PaymentMethodType.Credits:
    case PaymentMethodType.CreditsApplied:
    case PaymentMethodType.CreditNote:
    case PaymentMethodType.IssuedCredit:
      return (
        <ThemeIcon
          lightIcon={BwPaymentMethodCreditsLight}
          darkIcon={BwPaymentMethodCreditsDark}
          className={cn("h-10 w-15", className)}
        />
      );
    case PaymentMethodType.Deposit:
    case PaymentMethodType.DepositApplied:
      return (
        <ThemeIcon
          lightIcon={BwPaymentMethodDepositLight}
          darkIcon={BwPaymentMethodDepositDark}
          className={cn("h-10 w-15", className)}
        />
      );
  }

  switch (brand.toLowerCase()) {
    case "visa":
      return (
        <ThemeIcon
          lightIcon={BwPaymentMethodVisaLight}
          darkIcon={BwPaymentMethodVisaDark}
          className={cn("h-10 w-15", className)}
        />
      );
    case "mastercard":
      return (
        <ThemeIcon
          lightIcon={BwPaymentMethodMasterCardLight}
          darkIcon={BwPaymentMethodMasterCardDark}
          className={cn("h-10 w-15", className)}
        />
      );
    case "paypal":
      return (
        <ThemeIcon
          lightIcon={BwPaymentMethodPaypalLight}
          darkIcon={BwPaymentMethodPaypalDark}
          className={cn("h-10 w-15", className)}
        />
      );
    case "applepay":
      return (
        <ThemeIcon
          lightIcon={BwPaymentMethodApplePayLight}
          darkIcon={BwPaymentMethodApplePayDark}
          className={cn("h-10 w-15", className)}
        />
      );
    case "googlepay":
      return (
        <ThemeIcon
          lightIcon={BwPaymentMethodGooglePayLight}
          darkIcon={BwPaymentMethodGooglePayDark}
          className={cn("h-10 w-15", className)}
        />
      );
    case "alipay":
      return (
        <CustomCardIcon className={cn(className)}>
          <BwCardAlipay className="h-10 w-full rounded" />
        </CustomCardIcon>
      );
    case "amex":
      return (
        <CustomCardIcon className={cn(className)}>
          <BwCardAmex className="h-10 w-full rounded" />
        </CustomCardIcon>
      );
    case "diners":
      return (
        <CustomCardIcon className={cn(className)}>
          <BwCardDiners className="h-10 w-full rounded" />
        </CustomCardIcon>
      );
    case "discover":
      return (
        <CustomCardIcon className={cn(className)}>
          <BwCardDiscover className="h-10 w-full rounded" />
        </CustomCardIcon>
      );
    case "elo":
      return (
        <CustomCardIcon className={cn(className)}>
          <BwCardElo className="h-10 w-full rounded" />
        </CustomCardIcon>
      );
    case "hiper":
      return (
        <CustomCardIcon className={cn(className)}>
          <BwCardHiper className="h-10 w-full rounded" />
        </CustomCardIcon>
      );
    case "hipercard":
      return (
        <CustomCardIcon className={cn(className)}>
          <BwCardHipercard className="h-10 w-full rounded" />
        </CustomCardIcon>
      );
    case "jcb":
      return (
        <CustomCardIcon className={cn(className)}>
          <BwCardJcb className="h-10 w-full rounded" />
        </CustomCardIcon>
      );
    case "maestro":
      return (
        <CustomCardIcon className={cn(className)}>
          <BwCardMaestro className="h-10 w-full rounded" />
        </CustomCardIcon>
      );
    case "mir":
      return (
        <CustomCardIcon className={cn(className)}>
          <BwCardMir className="h-10 w-full rounded" />
        </CustomCardIcon>
      );
    case "unionpay":
      return (
        <CustomCardIcon className={cn(className)}>
          <BwCardUnionpay className="h-10 w-full rounded" />
        </CustomCardIcon>
      );
    default:
      return (
        <ThemeIcon
          lightIcon={BwPaymentMethodCcGenericLight}
          darkIcon={BwPaymentMethodCcGenericDark}
          className={cn("h-10 w-15", className)}
        />
      );
  }
};

const CustomCardIcon = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        /* sorted */
        "relative overflow-hidden",
        "shrink-0",
        "h-10 w-15",
        "rounded",
        className,
      )}
    >
      <div className="relative flex h-full w-full items-center justify-center">
        {children}
      </div>
    </div>
  );
};
